/**
 * Pictures for the clearing's tray (decision 0015): each thing alone on a tile in the mist, and
 * a tile of each ground, drawn in the world's own look by a small second renderer, one
 * a frame, and kept as images while the clearing is open. Where a second canvas gets no WebGL the
 * tray shows names only.
 */
import type { AtlasDef, ThemeDef } from "@mistsweeper/assets";
import { Terrain } from "@mistsweeper/core";
import {
  type ModelSet,
  type Pick,
  type Placement,
  WATER_DROP,
  WorldRenderer,
  type WorldSource,
} from "@mistsweeper/renderer";
import { fits, type Ground, naturalDress, placementOf } from "./clearing.ts";

/** A picture's side in CSS pixels; it is drawn at the screen's own resolution. */
export const THUMB_CSS = 88;
/** How much of the picture the thing fills. */
const FILL = 0.88;
/** A fixed moment, so the mist and the wind are the same in every picture. */
const FROZEN = 1000;
/** Below this a model is under the ground: a creature's body reaches down into its chasm. */
const UNDERGROUND = -0.05;
/**
 * A thing no higher than this above its foot, in its own units, is flat: its silhouette would
 * sink whole into the mist, so it stands this much above the ground instead, in tiles.
 */
const FLAT_TOP = 0.05;
const FLAT_LIFT = 0.03;

const TERRAIN: Readonly<Record<Ground, Terrain>> = {
  meadow: Terrain.grass,
  forest: Terrain.forest,
  water: Terrain.water,
  mountain: Terrain.mountain,
};

/** Whether a ground's own growth on a tile makes a good picture of it. */
const GOOD: Readonly<Record<Ground, (names: readonly string[]) => boolean>> = {
  meadow: (n) => n.some((m) => /juniper|bush|birch/.test(m)) && n.length >= 3,
  forest: (n) => n.filter((m) => /spruce|pine|birch/.test(m)).length >= 3,
  water: (n) => n.some((m) => m.includes("lilypads")) && n.some((m) => m.includes("reeds")),
  mountain: (n) => n.some((m) => m.includes("peak")),
};

/**
 * Keys: a model's name, `ghost:` and a model's name for its silhouette in the mist, a thing not
 * found yet, or `ground:` and a ground.
 */
export class Thumbnails {
  private readonly models: ModelSet;
  private readonly pick: Pick;
  private readonly size: number;
  private readonly canvas = document.createElement("canvas");
  private view: WorldRenderer | null = null;
  private failed = false;
  private readonly spot = { x: 0, y: 0 };
  private terrain: Terrain = Terrain.grass;
  private placements: Placement[] = [];
  private readonly world: WorldSource;
  private readonly done = new Map<string, string | null>();
  private readonly waiting = new Map<string, ((url: string | null) => void)[]>();
  private queue: string[] = [];
  private running = false;
  private readonly spots = new Map<Ground, { x: number; y: number }>();
  private readonly outlines = new Map<string, Float32Array>();

  constructor(
    models: ModelSet,
    atlas: { readonly def: AtlasDef; readonly image: HTMLImageElement },
    theme: ThemeDef,
    pick: Pick,
  ) {
    this.models = models;
    this.pick = pick;
    this.size = Math.round(THUMB_CSS * Math.min(3, Math.max(1, devicePixelRatio)));
    const at = (x: number, y: number) => x === this.spot.x && y === this.spot.y;
    this.world = {
      isRevealed: at,
      hasBanner: () => false,
      adjacent: () => 0,
      terrain: (x, y) => (at(x, y) ? this.terrain : Terrain.grass),
      isChasm: () => false,
      isClosed: () => false,
      isSettled: () => false,
      status: "playing",
      killer: null,
    };
    try {
      this.canvas.width = this.size;
      this.canvas.height = this.size;
      this.view = new WorldRenderer(this.canvas, {
        theme,
        scale: 2,
        dot: 1,
        // The finer halftone at every size: the pictures are small (Daniel, 2026-10-20).
        fineDots: { from: 1e6, to: 1e5 },
        lock: "world",
        reducedMotion: true,
        preserveDrawingBuffer: true,
        shadows: true,
        life: false,
        // Smoke rises far above a chimney: framed in, it would make the house small.
        smoke: false,
        // A thing stands bare on paper, and a ground keeps its tile plain.
        grass: false,
        maxScale: 60,
        atlas,
        models,
        dress: (x, y) => (at(x, y) ? this.placements : null),
      });
      this.view.resize(this.size, this.size);
    } catch {
      this.failed = true;
    }
  }

  /** The picture for a key, drawn when its turn comes; the first keys asked for come first. */
  request(key: string): Promise<string | null> {
    if (this.done.has(key)) return Promise.resolve(this.done.get(key) ?? null);
    if (this.failed) return Promise.resolve(null);
    return new Promise((resolve) => {
      const list = this.waiting.get(key);
      if (list) list.push(resolve);
      else {
        this.waiting.set(key, [resolve]);
        this.queue.push(key);
      }
      this.run();
    });
  }

  /** Moves these keys to the front of the queue: the tab the player is looking at. */
  prioritize(keys: readonly string[]): void {
    const front = new Set(keys);
    this.queue = [
      ...this.queue.filter((k) => front.has(k)),
      ...this.queue.filter((k) => !front.has(k)),
    ];
  }

  private run(): void {
    if (this.running) return;
    this.running = true;
    const step = () => {
      const key = this.queue.shift();
      if (key === undefined) {
        this.running = false;
        return;
      }
      void this.draw(key).then((url) => {
        this.done.set(key, url);
        for (const resolve of this.waiting.get(key) ?? []) resolve(url);
        this.waiting.delete(key);
        requestAnimationFrame(step);
      });
    };
    requestAnimationFrame(step);
  }

  private draw(key: string): Promise<string | null> {
    const view = this.view;
    if (!view) return Promise.resolve(null);
    try {
      if (key.startsWith("ground:")) {
        const ground = key.slice(7) as Ground;
        const spot = this.groundSpot(ground);
        this.stage(
          spot,
          TERRAIN[ground],
          naturalDress(this.worldAt(spot, ground), spot.x, spot.y, this.pick),
        );
        view.setBare(false);
        this.frame(0.5);
      } else {
        const ghost = key.startsWith("ghost:");
        const name = ghost ? key.slice(6) : key;
        const water = !fits(name, "meadow");
        // Alone on one tile in the mist, a massif stands on that tile's middle and its mist.
        const { span: _span, ...placement } = placementOf(name, 0, water);
        // A flat thing's silhouette would sink whole into the mist, every corner at its foot:
        // lifted just clear of the ground, it shows (Daniel, 2026-10-21).
        const lifted = ghost && this.top(name) <= FLAT_TOP ? { h: FLAT_LIFT } : {};
        this.stage({ x: 0, y: 0 }, water ? Terrain.water : Terrain.grass, [
          { ...placement, dx: 0, dy: 0, ...(ghost ? { ghost } : {}), ...lifted },
        ]);
        // A thing stands alone on paper, with no ground and no mist, and fills the picture
        // however small it is (Daniel, 2026-10-20).
        view.setBare(true);
        this.frame(0);
      }
      view.invalidate();
      view.render(this.world, FROZEN, null);
    } catch {
      return Promise.resolve(null);
    }
    return new Promise((resolve) => {
      this.canvas.toBlob((blob) => resolve(blob ? URL.createObjectURL(blob) : null), "image/png");
    });
  }

  private stage(spot: { x: number; y: number }, terrain: Terrain, placements: Placement[]): void {
    this.spot.x = spot.x;
    this.spot.y = spot.y;
    this.terrain = terrain;
    this.placements = placements;
  }

  /** The thumbnail world as the placement rules would see it with this ground on the spot. */
  private worldAt(spot: { x: number; y: number }, ground: Ground): WorldSource {
    const terrain = TERRAIN[ground];
    return {
      ...this.world,
      isRevealed: (x, y) => x === spot.x && y === spot.y,
      terrain: (x, y) => (x === spot.x && y === spot.y ? terrain : Terrain.grass),
    };
  }

  /** The first tile, searched in a fixed order, whose own growth shows the ground at its best. */
  private groundSpot(ground: Ground): { x: number; y: number } {
    const known = this.spots.get(ground);
    if (known) return known;
    let found = { x: 12, y: 7 };
    search: for (let y = 8; y < 40; y += 1) {
      for (let x = 8; x < 40; x += 1) {
        const names = naturalDress(this.worldAt({ x, y }, ground), x, y, this.pick).map(
          (p) => p.name,
        );
        if (GOOD[ground](names)) {
          found = { x, y };
          break search;
        }
      }
    }
    this.spots.set(ground, found);
    return found;
  }

  /**
   * Zooms and pans so the staged tile fills the picture: the models' points as placed, and a
   * square of ground `base` tiles from the middle each way, for a picture of the ground.
   */
  private frame(base: number): void {
    const view = this.view;
    if (!view) return;
    const level = this.terrain === Terrain.water ? -WATER_DROP : 0;
    const points: [number, number, number][] = [];
    for (const [sx, sy] of [
      [-1, -1],
      [1, -1],
      [-1, 1],
      [1, 1],
    ] as const) {
      points.push([this.spot.x + sx * base, this.spot.y + sy * base, level]);
    }
    for (const p of this.placements) {
      const outline = this.visible(p.name);
      const c = Math.cos(p.yaw);
      const s = Math.sin(p.yaw);
      for (let i = 0; i < outline.length; i += 3) {
        const mx = outline[i] ?? 0;
        const my = outline[i + 1] ?? 0;
        const mz = outline[i + 2] ?? 0;
        points.push([
          this.spot.x + p.dx + p.scale * (mx * c + mz * s),
          this.spot.y + p.dy + p.scale * (-mx * s + mz * c),
          (p.h ?? 0) + p.scale * my,
        ]);
      }
    }
    const half = this.size / 2;
    const bounds = () => {
      let x0 = Number.POSITIVE_INFINITY;
      let y0 = Number.POSITIVE_INFINITY;
      let x1 = Number.NEGATIVE_INFINITY;
      let y1 = Number.NEGATIVE_INFINITY;
      for (const [x, y, h] of points) {
        const d = view.pointToDevice(x, y, h);
        x0 = Math.min(x0, d.x);
        y0 = Math.min(y0, d.y);
        x1 = Math.max(x1, d.x);
        y1 = Math.max(y1, d.y);
      }
      return { w: x1 - x0, h: y1 - y0, cx: (x0 + x1) / 2, cy: (y0 + y1) / 2 };
    };
    view.centreOnTile(this.spot.x, this.spot.y);
    const before = bounds();
    view.zoomAround(
      (view.scale * (this.size * FILL)) / Math.max(1, before.w, before.h),
      half,
      half,
    );
    const after = bounds();
    view.panBy(half - after.cx, half - after.cy);
  }

  /** How high a model reaches above its foot, in its own units. */
  private top(name: string): number {
    const outline = this.visible(name);
    let top = Number.NEGATIVE_INFINITY;
    for (let i = 1; i < outline.length; i += 3) top = Math.max(top, outline[i] ?? 0);
    return top;
  }

  /**
   * The points of the part of a model that stands above the ground, in the model's own units:
   * its own outline frames it tighter than a box would, so a house fills its picture.
   */
  private visible(name: string): Float32Array {
    const known = this.outlines.get(name);
    if (known) return known;
    const position = this.models.models.get(name)?.geometry.getAttribute("position");
    const kept: number[] = [];
    for (let i = 0; position && i < position.count; i += 1) {
      const y = position.getY(i);
      if (y >= UNDERGROUND) kept.push(position.getX(i), y, position.getZ(i));
    }
    const outline = new Float32Array(kept);
    this.outlines.set(name, outline);
    return outline;
  }
}
